import { safeSetItem } from "@/lib/storage/safe-local";
/**
 * Storefront tracking helper.
 *
 * Fire-and-forget by design: every call returns immediately and failures are
 * swallowed, so nothing in the shopping, checkout, or auth flow can ever be
 * slowed down or blocked by analytics. No Customer.io credential is present
 * in the browser bundle — the event is relayed through a server function.
 */
import { trackStorefrontEvent, identifyStorefrontCustomer } from "./customerio.functions";
import type { CioEventName, CioEventPayload } from "./events";
import { phCapture, phIdentify, phReset } from "@/lib/analytics/posthog";
import { toPostHogEvent } from "@/lib/analytics/events";

const SESSION_KEY = "cb16.cio.session";

function sessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function device(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window.innerWidth;
  return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

function browser(): string | undefined {
  if (typeof navigator === "undefined") return undefined;
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Other";
}

function readIdentity(): { email?: string; customer_id?: string } {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem("cb16.cio.identity");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Remember who the current visitor is so later events can be attributed. */
export function rememberIdentity(email?: string | null, customerId?: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (!email && !customerId) {
      window.localStorage.removeItem("cb16.cio.identity");
      phReset();
    }
    else
      safeSetItem(
        "cb16.cio.identity",
        JSON.stringify({ email: email ?? undefined, customer_id: customerId ?? undefined }),
      );
  } catch {
    /* ignore */
  }
}

function sanitise(payload: CioEventPayload): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string") out[k] = v.slice(0, 500);
    else if (typeof v === "number" || typeof v === "boolean") out[k] = v;
    else out[k] = String(v).slice(0, 500);
  }
  return out;
}

/** Track a storefront event. Never throws, never awaited by callers. */
export function track(name: CioEventName, payload: CioEventPayload = {}) {
  if (typeof window === "undefined") return;
  // Set by the TEST MODE banner: no marketing/analytics events during payment tests.
  if ((window as { __CB16_PAYMENT_TEST_MODE__?: boolean }).__CB16_PAYMENT_TEST_MODE__) return;
  const identity = readIdentity();
  const body = sanitise({
    ...identity,
    ...payload,
    device: device(),
    browser: browser(),
    session_id: sessionId(),
    timestamp: new Date().toISOString(),
  });
  void Promise.resolve()
    .then(() => trackStorefrontEvent({ data: { name, payload: body } }))
    .catch(() => undefined);

  // Single fan-out point to PostHog — mapped names only, so an event is
  // never captured twice.
  const phName = toPostHogEvent(name);
  if (phName) phCapture(phName, body);
}

/** Create/update the Customer.io profile for a known visitor. */
export function identify(
  email: string,
  customerId?: string | null,
  attributes: Record<string, string | number | boolean | null> = {},
) {
  if (typeof window === "undefined" || !email) return;
  rememberIdentity(email, customerId);
  phIdentify(customerId || email, { email, ...attributes });
  void Promise.resolve()
    .then(() =>
      identifyStorefrontCustomer({
        data: { email, customerId: customerId ?? undefined, attributes },
      }),
    )
    .catch(() => undefined);
}